// Detect HP drops on the local player's city across snapshots and surface
// them as ONE grouped toast: consecutive hits aggregate (×count, total
// damage, per-hit detail list) instead of stacking identical cards. The
// toast auto-dismisses after a quiet period; each new hit refreshes the
// timer. Reset / capture branches resync the tracking ref so we don't fire
// false positives.

import type { GameSnapshot } from '@aod/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cityOwnerId } from '../ownership.js';
import type { AttackHit, AttackToast } from '../ui/AttackToastStack.js';

/** Toast stays this long after the most recent hit (ms). */
const TOAST_DURATION_MS = 4500;
/** Per-hit details kept for the expanded view (newest last). */
const MAX_HITS = 12;

export interface UseAttackToastsResult {
  readonly toasts: ReadonlyArray<AttackToast>;
  readonly dismiss: (id: number) => void;
}

export function useAttackToasts(
  snapshot: GameSnapshot | null,
  localPlayerIndex: number,
): UseAttackToastsResult {
  const [toasts, setToasts] = useState<ReadonlyArray<AttackToast>>([]);
  const lastHpRef = useRef<number | null>(null);
  const nextIdRef = useRef(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const hp = readLocalCityHp(snapshot, localPlayerIndex);
    if (hp === null) return;

    const previous = lastHpRef.current;
    lastHpRef.current = hp;
    if (previous === null || hp >= previous) return;

    const damage = previous - hp;
    const id = nextIdRef.current;
    setToasts((prev) => {
      const existing = prev[0];
      if (existing) {
        return [
          {
            ...existing,
            count: existing.count + 1,
            damage: existing.damage + damage,
            hpRemaining: hp,
            hits: [...existing.hits, { damage, hpRemaining: hp }].slice(-MAX_HITS),
          },
        ];
      }
      nextIdRef.current += 1;
      return [
        {
          id,
          count: 1,
          damage,
          hpRemaining: hp,
          hits: [{ damage, hpRemaining: hp }],
        },
      ];
    });

    // Refresh the auto-dismiss on every hit.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setToasts([]);
    }, TOAST_DURATION_MS);
  }, [snapshot, localPlayerIndex]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return { toasts, dismiss };
}

function readLocalCityHp(snapshot: GameSnapshot | null, ownerId: number): number | null {
  const children = snapshot?.scene.children ?? [];
  const myCity = children.find((n) => n.type === 'city' && cityOwnerId(n.props) === ownerId);
  const hp = myCity?.props?.hp as number | undefined;
  return hp ?? null;
}
